// The admin console (.design-specs/admin-console.plan.md): one page, one tab per question, the active tab in
// `?tab=`. Read-only by design (R1), with two named exceptions: "Sync now" on the Integrations tab, and the Tutorials
// tab, which edits the Settings › Tutorials list (ART-293). Each tab reads its own endpoint, so a tab can
// grow without reworking the others; only the active tab mounts, so its reads run only when someone looks.
import { Button, Card, Page, Tabs } from "@cntrl/ui";
import {
  isSecondFactorRequired,
  useAdminAnomalies,
  useAdminBands,
  useAdminIntegrations,
  useAdminPeople,
  useAdminPush,
} from "../../app/hooks/useAdmin.js";
import { ADMIN_TABS, type AdminTab } from "./adminTabs.js";
import { BandsTab, PeopleTab, SocialsTab } from "./AdminTenantTabs.js";
import { AnomaliesTab, PushTab } from "./AdminHealthTabs.js";
import { IntegrationsTab } from "./AdminIntegrationsTab.js";
import { VendorUsageTab } from "./AdminVendorUsageTab.js";
import { TutorialsTab } from "./AdminTutorialsTab.js";
import { ApplicationsTab } from "./AdminApplicationsTab.js";
import { exact } from "./adminFormat.js";
import styles from "./AdminDashboard.module.scss";

export interface AdminConsoleProps {
  activeTab: AdminTab;
  onTab: (tab: AdminTab) => void;
  /** The band the Integrations tab's Band sync card shows (`?band=`). */
  bandId?: string;
  onBand: (bandId: string | undefined) => void;
  /** The application the Applications tab has open (`?id=`, which the new-application alert links to). */
  applicationId?: string;
  onApplication?: (applicationId: string | undefined) => void;
  /** Sign out and back in — the remedy when production refuses a session with no second factor. */
  onSignInAgain: () => void;
}

/** One overview tile: a headline number, a line under it, and the tab it opens. */
function Tile({
  label, value, sub, alert, onOpen,
}: { label: string; value: string; sub?: string; alert?: boolean; onOpen: () => void }) {
  return (
    <button type="button" className={styles.tile} onClick={onOpen} data-alert={alert || undefined}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={styles.tileValue}>{value}</span>
      {sub && <span className={styles.tileSub}>{sub}</span>}
    </button>
  );
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const within = (iso: string | null | undefined, ms: number) => !!iso && Date.now() - new Date(iso).getTime() <= ms;

function OverviewTab({ onTab }: { onTab: (tab: AdminTab) => void }) {
  const bands = useAdminBands();
  const people = useAdminPeople();
  const anomalies = useAdminAnomalies();
  const integrations = useAdminIntegrations();
  const push = useAdminPush();

  const b = bands.data ?? [];
  const realBands = b.filter((x) => !x.isFixture);
  const paying = b.filter((x) => ["active", "lifetime", "past_due"].includes(x.planStatus ?? "")).length;
  const pro = b.filter((x) => x.tier === "pro").length;
  const p = people.data ?? [];
  const activeWeek = p.filter((x) => within(x.lastSeenOn, WEEK_MS)).length;
  const open = anomalies.data?.open?.length ?? 0;
  const watch = anomalies.data?.watch?.length ?? 0;
  const attention = integrations.data?.attentionTotal ?? 0;
  const devices = (push.data?.devices ?? []).filter((d) => d.enabled).reduce((n, d) => n + (d.devices ?? 0), 0);
  const pending = (q: { isLoading: boolean }) => (q.isLoading ? "…" : undefined);

  return (
    <div className={styles.tiles}>
      <Tile label="Bands" value={pending(bands) ?? exact(b.length)}
        sub={`${realBands.length} real · ${b.length - realBands.length} fixture · ${paying} paying · ${pro} Pro`}
        onOpen={() => onTab("bands")} />
      <Tile label="People" value={pending(people) ?? exact(p.length)}
        sub={`${activeWeek} signed in this week`} onOpen={() => onTab("people")} />
      <Tile label="Open anomalies" value={pending(anomalies) ?? exact(open)} alert={open > 0}
        sub={`${watch} on the new-connection watch list`} onOpen={() => onTab("anomalies")} />
      <Tile label="Sync jobs needing attention" value={pending(integrations) ?? exact(attention)} alert={attention > 0}
        sub="Blocked, stale or failing" onOpen={() => onTab("integrations")} />
      <Tile label="Push devices" value={pending(push) ?? exact(devices)} alert={push.data?.authFailureRecently === true}
        sub={push.data?.authFailureRecently ? "Apple is rejecting this lane's key" : "Enabled, all platforms"}
        onOpen={() => onTab("push")} />
      <Tile label="Socials" value={pending(bands) ?? exact(b.filter((x) => (x.connectedProviders?.length ?? 0) > 0).length)}
        sub="Bands with at least one connection" onOpen={() => onTab("socials")} />
    </div>
  );
}

export function AdminConsole({
  activeTab, onTab, bandId, onBand, applicationId, onApplication, onSignInAgain,
}: AdminConsoleProps) {
  // Every admin endpoint refuses the same way, so one read decides: production refuses a password-only session,
  // and the screen offers the remedy instead of an error on every tab. Bands is read on most tabs anyway (cached).
  const gate = useAdminBands();
  if (isSecondFactorRequired(gate.error)) {
    return (
      <Page title="Platform health">
        <Card title="Sign in with a second factor">
          <div className={styles.stack}>
            <p>
              The admin console needs a session signed in with your passkey or the emailed code. Sign out and back
              in to continue.
            </p>
            <div>
              <Button variant="primary" size="sm" onClick={onSignInAgain}>
                Sign in again
              </Button>
            </div>
          </div>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Platform health">
      <Tabs tabs={ADMIN_TABS} active={activeTab} onChange={onTab} label="Admin sections" />
      <div className={styles.stack} role="tabpanel" aria-label={ADMIN_TABS.find((t) => t.key === activeTab)?.label}>
        {activeTab === "overview" && <OverviewTab onTab={onTab} />}
        {activeTab === "applications" && <ApplicationsTab applicationId={applicationId} onSelect={onApplication ?? (() => {})} />}
        {activeTab === "bands" && <BandsTab />}
        {activeTab === "socials" && <SocialsTab />}
        {activeTab === "people" && <PeopleTab />}
        {activeTab === "anomalies" && <AnomaliesTab />}
        {activeTab === "integrations" && <IntegrationsTab bandId={bandId} onBand={onBand} />}
        {activeTab === "api-usage" && <VendorUsageTab />}
        {activeTab === "push" && <PushTab />}
        {activeTab === "tutorials" && <TutorialsTab />}
      </div>
    </Page>
  );
}
